import React, { useMemo, useState } from 'react';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { curveMonotoneX } from '@visx/curve';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import type { HomeDashboard } from '../../../api/homeV3Api';
import styles from '../HomeV3.module.css';

interface Props { points: HomeDashboard['requestSummary']['dailyActivity']; }

export function RequestPulseChart({ points }: Props) {
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
    const parsed = useMemo(() => points.map((point) => ({ ...point, parsedDate: new Date(`${point.date}T00:00:00`) })), [points]);
    const submitted = points.reduce((sum, point) => sum + point.submittedCount, 0);
    const completed = points.reduce((sum, point) => sum + point.completedCount, 0);

    if (points.length === 0) return <div className={styles.chartEmpty}>No request activity in the last 30 days.</div>;

    return (
        <div>
            <div className={styles.chartLegend} aria-label="Request pulse totals">
                <span><i className={styles.submittedSwatch} />Submitted: {submitted}</span>
                <span><i className={styles.completedSwatch} />Completed: {completed}</span>
            </div>
            <div className={styles.chartCanvas}>
                <ParentSize debounceTime={50}>{({ width, height }) => {
                    const margin = { top: 15, right: 18, bottom: 34, left: 34 };
                    const innerWidth = Math.max(1, width - margin.left - margin.right);
                    const innerHeight = Math.max(1, height - margin.top - margin.bottom);
                    const first = parsed[0].parsedDate;
                    const last = parsed[parsed.length - 1].parsedDate;
                    const xDomain: [Date, Date] = first.getTime() === last.getTime()
                        ? [new Date(first.getTime() - 43_200_000), new Date(last.getTime() + 43_200_000)]
                        : [first, last];
                    const x = scaleTime({ domain: xDomain, range: [0, innerWidth] });
                    const maxValue = Math.max(1, ...parsed.flatMap((point) => [point.submittedCount, point.completedCount]));
                    const y = scaleLinear({ domain: [0, maxValue], range: [innerHeight, 0], nice: true });
                    return (
                        <svg width={width} height={height} role="img" aria-label={`30-day request pulse: ${submitted} submitted and ${completed} completed`}>
                            <Group left={margin.left} top={margin.top}>
                                <AreaClosed data={parsed} x={(point) => x(point.parsedDate)} y={(point) => y(point.submittedCount)} yScale={y} curve={curveMonotoneX} fill="var(--home-brand-soft)" />
                                <LinePath data={parsed} x={(point) => x(point.parsedDate)} y={(point) => y(point.submittedCount)} curve={curveMonotoneX} stroke="#6667b5" strokeWidth={2} />
                                <LinePath data={parsed} x={(point) => x(point.parsedDate)} y={(point) => y(point.completedCount)} curve={curveMonotoneX} stroke="#18794e" strokeWidth={2} />
                                {parsed.map((point, index) => (
                                    <g key={point.date}>
                                        <circle
                                            cx={x(point.parsedDate)} cy={y(point.submittedCount)} r={focusedIndex === index ? 5 : 3}
                                            fill="#6667b5" tabIndex={0}
                                            aria-label={`${point.date}: ${point.submittedCount} submitted, ${point.completedCount} completed`}
                                            onFocus={() => setFocusedIndex(index)} onBlur={() => setFocusedIndex(null)}
                                            onMouseEnter={() => setFocusedIndex(index)} onMouseLeave={() => setFocusedIndex(null)}
                                        />
                                        <circle cx={x(point.parsedDate)} cy={y(point.completedCount)} r={3} fill="#18794e" aria-hidden="true" />
                                    </g>
                                ))}
                                <AxisLeft scale={y} numTicks={4} hideAxisLine hideTicks tickLabelProps={{ fill: 'currentColor', fontSize: 10, textAnchor: 'end', dy: '0.33em' }} />
                                <AxisBottom top={innerHeight} scale={x} numTicks={4} hideAxisLine tickFormat={(value) => new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(value as Date)} tickLabelProps={{ fill: 'currentColor', fontSize: 10, textAnchor: 'middle' }} />
                            </Group>
                        </svg>
                    );
                }}</ParentSize>
            </div>
            <div className={styles.chartTooltip} aria-live="polite">
                {focusedIndex === null ? 'Focus a point for daily values.' : `${points[focusedIndex].date}: ${points[focusedIndex].submittedCount} submitted and ${points[focusedIndex].completedCount} completed.`}
            </div>
            <details className={styles.chartValues}>
                <summary>View daily values</summary>
                <ul>{points.map((point) => <li key={point.date}>{point.date}: {point.submittedCount} submitted, {point.completedCount} completed</li>)}</ul>
            </details>
        </div>
    );
}
